// useProductSelection.ts
import { useState, useEffect, useCallback } from 'react';
import logger, { Logger } from '../utils/logger';

// Product type definitions (from original component)
interface Product {
  quantity: number;
  _id: string;
  name: string;
  description?: string;
  price: number;
  category?: {
    _id: string;
    name: string;
  };
  extraDetails?: {
    unit?: string;
    [key: string]: any;
  };
  images?: Array<{
    filePath: string;
    fileName: string;
  }>;
}

interface ProductWithQuantity extends Product {
  id?: string;
  quantity: number; // Can be decimal
  saleValue?: number; // Manual override for sale value
}

interface UseProductSelectionProps {
  isOpen: boolean;
  initialSelectedProducts?: ProductWithQuantity[];
  onProductsSelect: (products: ProductWithQuantity[]) => void;
}

export const useProductSelection = ({
  isOpen,
  initialSelectedProducts = [],
  onProductsSelect
}: UseProductSelectionProps) => {
  const [selectedProducts, setSelectedProducts] = useState<Map<string, ProductWithQuantity>>(new Map());

  // Initialize selected products when popup opens
  useEffect(() => {
    if (!isOpen) return;

    logger.log('🔄 useProductSelection: Initializing selected products');
    logger.log('🔍 Initial products count:', initialSelectedProducts?.length);
    
    const initialProductsMap = new Map<string, ProductWithQuantity>();
    
    if (initialSelectedProducts?.length > 0) {
      initialSelectedProducts.forEach((product, index) => {
        const productId = product._id || product.id;
        
        if (!productId) {
          console.error(`❌ Product ${index} missing ID:`, product);
          return;
        }
        
        initialProductsMap.set(productId, {
          ...product,
          _id: productId,
          quantity: product.quantity || 1
        });
        
        logger.log(`✅ Added product ${productId} to selection`);
      });
    }
    
    logger.log('📊 Final selection map size:', initialProductsMap.size);
    
    // Only update state if the new map is actually different
    setSelectedProducts(prev => {
      if (prev.size === initialProductsMap.size) {
        let isEqual = true;
        for (const [key, value] of initialProductsMap) {
          const prevValue = prev.get(key);
          if (!prevValue || prevValue.quantity !== value.quantity) {
            isEqual = false;
            break;
          }
        }
        if (isEqual) {
          logger.log('⏭️ Skipping state update - maps are identical');
          return prev;
        }
      }
      return initialProductsMap;
    });
  }, [isOpen]); // Only depend on isOpen - the initialSelectedProducts are handled by the comparison above

  // Toggle product selection
  const handleToggleProduct = useCallback((product: Product) => {
    Logger.log('🔄 Toggling product:', product.name);
    
    setSelectedProducts(prevSelected => {
      const newMap = new Map(prevSelected);
      
      if (newMap.has(product._id)) {
        logger.log('➖ Removing product:', product._id);
        newMap.delete(product._id);
      } else {
        logger.log('➕ Adding product:', product._id);
        newMap.set(product._id, { ...product, quantity: 1 });
      }
      
      return newMap;
    });
  }, []);

  // Update product quantity
// Update product quantity
const handleQuantityChange = useCallback((productId: string, changeOrValue: number) => {
  Logger.log('🔄 Changing quantity for:', productId, 'Change:', changeOrValue);
  
  if (!productId || isNaN(changeOrValue)) {
    console.warn('⚠️ Invalid quantity update:', { productId, changeOrValue });
    return;
  }
  
  setSelectedProducts(prevSelected => {
    const newMap = new Map(prevSelected);
    const product = newMap.get(productId);
    
    if (product) {
      let newQuantity;
      
      // If changeOrValue is between -10 and 10, treat as an increment
      if (changeOrValue >= -10 && changeOrValue <= 10 && changeOrValue !== 0) {
        newQuantity = product.quantity + changeOrValue;
      } else {
        // Otherwise treat as absolute value
        newQuantity = changeOrValue;
      }
      
      // Ensure minimum quantity and fix decimal precision
      const validQuantity = Math.max(1, parseFloat(newQuantity.toFixed(2)));
      
      logger.log('📊 Quantity update:', {
        product: product.name,
        old: product.quantity,
        new: validQuantity,
        clearingSaleValue: !!product.saleValue
      });
      
      // Clear manual sale value when quantity changes
      newMap.set(productId, { 
        ...product, 
        quantity: validQuantity,
        saleValue: undefined  // Reset manual override
      });
    }
    
    return newMap;
  });
}, []);

// Handle manual sale value input
const handleSaleValueChange = useCallback((productId: string, value: string) => {
  Logger.log('💰 Changing sale value for:', productId, 'Value:', value);
  
  if (!productId) {
    console.warn('⚠️ Invalid sale value update: missing productId');
    return;
  }
  
  setSelectedProducts(prevSelected => {
    const newMap = new Map(prevSelected);
    const product = newMap.get(productId);
    
    if (product) {
      // Set manual sale value, undefined if empty or invalid
      const numericValue = value && !isNaN(parseFloat(value)) ? parseFloat(value) : undefined;
      
      logger.log('💰 Sale value update:', {
        product: product.name,
        oldSaleValue: product.saleValue,
        newSaleValue: numericValue,
        calculatedValue: product.price * product.quantity
      });
      
      newMap.set(productId, { ...product, saleValue: numericValue });
    }
    
    return newMap;
  });
}, []);

  // Calculate totals

const calculateTotals = useCallback(() => {
  let itemCount = 0;
  let subtotal = 0;
  
  selectedProducts.forEach(product => {
    itemCount += product.quantity;
    // Use manual sale value if set, otherwise calculate from price * quantity
    const productTotal = product.saleValue || (product.price * product.quantity);
    subtotal += productTotal;
  });
  
  logger.log('💰 Calculated totals:', { 
    itemCount, 
    subtotal,
    manualOverrides: Array.from(selectedProducts.values()).filter(p => p.saleValue).length
  });
  return { itemCount, subtotal };
}, [selectedProducts]);

 // Handle confirmation
const handleConfirm = useCallback((manualTotal?: string, source?: string) => {
  const productsArray = Array.from(selectedProducts.values());
  
  // Add manual total to the data if provided
  if (source === 'lead' && manualTotal && !isNaN(parseFloat(manualTotal))) {
    const totalValue = parseFloat(manualTotal);
    logger.log('✅ Confirming selection with manual total:', {
      products: productsArray.length,
      manualTotal: totalValue,
      calculatedTotal: productsArray.reduce((sum, p) => sum + (p.saleValue || p.price * p.quantity), 0)
    });
    
    // Add manual total as a property
    (productsArray as any).manualSaleValue = totalValue;
  } else {
    logger.log('✅ Confirming selection:', productsArray.length, 'products');
  }
  
  onProductsSelect(productsArray);
}, [selectedProducts, onProductsSelect]);

 return {
  selectedProducts,
  handleToggleProduct,
  handleQuantityChange,
  handleSaleValueChange,  // NEW: Add sale value handler
  calculateTotals,
  handleConfirm
};
};